// The single snake_case -> camelCase mapping site for the GIS endpoints.
// One pure function per endpoint; view types stay camelCase everywhere downstream.

import {
  deriveIssueStatus,
  type IssueDerivedStatus,
  type IssueSeverity,
  type IssueStatus,
} from '@/shared/constants/issue-status'

import { GIS_DISTRICT_NAME, MANDAL_CENTROIDS } from './mandal-centroids'

import type {
  GisDeltas,
  GisEntity,
  GisIssue,
  GisMandalsData,
  GisPeriodInfo,
  GisSite,
  GisSummary,
  GisTopPerformer,
  GisTrends,
  MandalAggregate,
  SiteStatus,
} from './types'

const DISTRICT_CENTER: readonly [number, number] = [79.07, 17.43]

// ---- raw (snake_case) wire shapes ---------------------------------------------------

interface RawDeltas {
  coverage_pct: number | null
  active_issues: number | null
  officer_avg_completion_pct: number | null
}

interface RawPeriod {
  label: string
  from: string
  to: string
  prev_from: string
  prev_to: string
}

interface RawTopPerformer {
  officer_id: string
  name: string
  completion_pct: number
}

interface RawMandalRow {
  mandal: string
  mandal_id?: string // P0; optional until backend deploys
  site_count: number
  coverage_pct: number | null
  geocoded_pct: number | null
  active_issues: number
  critical_issues: number
  avg_resolution_days: number | null
  officer_count: number
  officer_avg_completion_pct: number | null
  // P1 insight fields — optional so a not-yet-deployed backend degrades to null/0, not a crash.
  critical_share_pct?: number | null
  health_score?: number | null
  top_performer?: RawTopPerformer | null
  officers_below_60_count?: number
  surveyed_sites: number
  in_progress_sites: number
  overdue_sites: number
  never_surveyed_sites: number
  // Open-issue tallies feeding the Attention Score (backend request #2). `open_issues==active_issues`
  // and `open_critical==critical_issues`; `open_overdue` (open, non-critical, past its target) is the
  // genuinely-new term. Optional → fall back to active/critical counts and 0 if a backend lags.
  open_issues?: number
  open_critical?: number
  open_overdue?: number
  deltas: RawDeltas
}

interface RawSummary {
  mandal_count: number
  site_count: number
  unmapped_sites: number
  coverage_pct: number | null
  geocoded_pct: number | null
  active_issues: number
  critical_issues: number
  critical_share_pct: number | null
  avg_resolution_days: number | null
  officer_count: number
  officer_avg_completion_pct: number | null
  health_score: number
  top_performer: RawTopPerformer | null
  officers_below_60_count: number
  deltas: RawDeltas
}

export interface RawGisMandalsResponse {
  as_of: string
  period: RawPeriod
  entity_id: string | null
  summary: RawSummary
  mandals: RawMandalRow[]
}

// ---- mappers ------------------------------------------------------------------------

function mapDeltas(d: RawDeltas): GisDeltas {
  return {
    coverage: d.coverage_pct,
    activeIssues: d.active_issues,
    officers: d.officer_avg_completion_pct,
  }
}

function mapTopPerformer(t: RawTopPerformer | null | undefined): GisTopPerformer | null {
  return t ? { officerId: t.officer_id, name: t.name, completion: t.completion_pct } : null
}

function mapPeriod(p: RawPeriod): GisPeriodInfo {
  return { label: p.label, from: p.from, to: p.to, prevFrom: p.prev_from, prevTo: p.prev_to }
}

function centroidFor(mandal: string): readonly [number, number] {
  return MANDAL_CENTROIDS[mandal] ?? DISTRICT_CENTER
}

function mapMandalRow(r: RawMandalRow): MandalAggregate {
  return {
    name: r.mandal,
    mandalId: r.mandal_id ?? null,
    centroid: centroidFor(r.mandal),
    siteCount: r.site_count,
    coverage: r.coverage_pct,
    geocoded: r.geocoded_pct,
    activeIssues: r.active_issues,
    criticalIssues: r.critical_issues,
    avgResolutionDays: r.avg_resolution_days,
    officerCount: r.officer_count,
    officerAvgCompletion: r.officer_avg_completion_pct,
    criticalSharePct: r.critical_share_pct ?? null,
    healthScore: r.health_score ?? null,
    topPerformer: mapTopPerformer(r.top_performer),
    officersBelow60Count: r.officers_below_60_count ?? 0,
    surveyedSites: r.surveyed_sites,
    inProgressSites: r.in_progress_sites,
    overdueSites: r.overdue_sites,
    neverSurveyedSites: r.never_surveyed_sites,
    openIssues: r.open_issues ?? r.active_issues,
    openCritical: r.open_critical ?? r.critical_issues,
    openOverdue: r.open_overdue ?? 0,
    deltas: mapDeltas(r.deltas),
  }
}

function mapSummary(s: RawSummary): GisSummary {
  return {
    mandalCount: s.mandal_count,
    siteCount: s.site_count,
    unmappedSites: s.unmapped_sites,
    coverage: s.coverage_pct,
    geocoded: s.geocoded_pct,
    activeIssues: s.active_issues,
    criticalIssues: s.critical_issues,
    criticalSharePct: s.critical_share_pct,
    avgResolutionDays: s.avg_resolution_days,
    officerCount: s.officer_count,
    officerAvgCompletion: s.officer_avg_completion_pct,
    healthScore: s.health_score,
    topPerformer: mapTopPerformer(s.top_performer),
    officersBelow60Count: s.officers_below_60_count,
    deltas: mapDeltas(s.deltas),
  }
}

export function mapMandals(raw: RawGisMandalsResponse): GisMandalsData {
  return {
    district: GIS_DISTRICT_NAME,
    asOf: raw.as_of,
    period: mapPeriod(raw.period),
    entityId: raw.entity_id,
    summary: mapSummary(raw.summary),
    mandals: raw.mandals.map(mapMandalRow),
  }
}

// ---- sites --------------------------------------------------------------------------

interface RawSite {
  id: string
  name: string
  mandal: string
  mandal_id?: string | null // P0; optional until backend deploys, null when unmapped
  entity_id: string | null
  lat: number | null
  long: number | null
  survey_status: SiteStatus
  completion_pct: number
  last_survey_date: string | null
  officer_id?: string | null // allocated in-charge officer (sites.assigned_officer_id); null = unassigned
  officer_name?: string | null
}

export interface RawGisSitesResponse {
  as_of: string
  count: number
  sites: RawSite[]
}

function mapSite(s: RawSite): GisSite {
  return {
    id: s.id,
    name: s.name,
    mandal: s.mandal,
    mandalId: s.mandal_id ?? null,
    entityId: s.entity_id,
    lon: s.long,
    lat: s.lat,
    status: s.survey_status,
    completion: s.completion_pct,
    lastSurvey: s.last_survey_date,
    officerId: s.officer_id ?? null,
    officerName: s.officer_name ?? null,
  }
}

export function mapSites(raw: RawGisSitesResponse): GisSite[] {
  return raw.sites.map(mapSite)
}

// ---- entities (entity filter) -------------------------------------------------------

interface RawGisEntity {
  id: string
  name: string
  sites_count: number
  issues_active: number
}

export interface RawGisEntitiesResponse {
  entities: RawGisEntity[]
  total_count: number
}

export function mapEntities(raw: RawGisEntitiesResponse): GisEntity[] {
  return raw.entities.map((e) => ({
    id: e.id,
    name: e.name,
    sitesCount: e.sites_count,
    issuesActive: e.issues_active,
  }))
}

// ---- issues (B1/B4) -----------------------------------------------------------------
// Subset of GET /issues (owned by the backend, consumed by the `issues` system too; ESLint
// boundaries forbid importing theirs, so we re-declare the fields the GIS view reads).

export interface RawGisIssue {
  id: string
  issue_ref?: string | null
  title: string
  severity: IssueSeverity
  status: IssueStatus
  /** Server-derived display status (v2); optional so a lagging backend degrades gracefully. */
  derived_status?: IssueDerivedStatus
  /** True when pending past its resolution_eta (v2; server-computed). */
  is_overdue?: boolean
  /** HOD-committed resolution date, set on acknowledge (v2); null before acknowledge. */
  resolution_eta?: string | null
  entity_id: string
  entity_name?: string | null
  site_id?: string | null
  site_name?: string | null
  section_title?: string | null
  /** The survey question the issue was raised from; null/absent = no question linkage. */
  question_text?: string | null
  department_name?: string | null
  department?: { id?: string | null; name?: string | null } | null
  created_at: string
  photo_url?: string | null
  description?: string | null
}

/** Pagination block on the `/issues` response (snake_case); every field optional/defensive. */
export interface RawIssuesPagination {
  current_page?: number
  page_size?: number
  total_pages?: number
  total_count?: number
  has_next?: boolean
  has_previous?: boolean
}

/** Either the `{ issues, pagination }` envelope or a bare array — like the shipping `getIssues`. */
export type RawGisIssuesResponse =
  | RawGisIssue[]
  | { issues?: RawGisIssue[] | null; pagination?: RawIssuesPagination | null }

/** Owning department name from the nested `department.name`, else the flat `department_name`. */
function issueDept(i: RawGisIssue): string | null {
  return i.department?.name ?? i.department_name ?? null
}

/** v2 lifecycle passthrough (derived_status / is_overdue / resolution_eta) with safe fallbacks. */
function issueLifecycle(i: RawGisIssue): Pick<GisIssue, 'derivedStatus' | 'isOverdue' | 'resolutionEta'> {
  return {
    derivedStatus: i.derived_status ?? deriveIssueStatus(i.status, i.is_overdue),
    isOverdue: i.is_overdue ?? false,
    resolutionEta: i.resolution_eta ?? null,
  }
}

export function mapIssue(i: RawGisIssue): GisIssue {
  return {
    id: i.id,
    issueRef: i.issue_ref ?? null,
    title: i.title,
    severity: i.severity,
    status: i.status,
    ...issueLifecycle(i),
    siteId: i.site_id ?? null,
    siteName: i.site_name ?? null,
    entityName: i.entity_name ?? null,
    department: issueDept(i),
    category: i.section_title ?? null,
    question: i.question_text ?? null,
    createdAt: i.created_at,
    photoUrl: i.photo_url ?? null,
    description: i.description ?? null,
  }
}

// ---- trends -------------------------------------------------------------------------

export interface RawGisTrendsResponse {
  period: string
  bucket_size: string
  buckets: string[]
  series: {
    coverage_pct: (number | null)[]
    active_issues: (number | null)[]
    officer_completion_pct: (number | null)[]
  }
}

export function mapTrends(raw: RawGisTrendsResponse): GisTrends {
  return {
    bucketSize: raw.bucket_size,
    buckets: raw.buckets,
    series: {
      coverage: raw.series.coverage_pct,
      issues: raw.series.active_issues,
      officers: raw.series.officer_completion_pct,
    },
  }
}
